import { useCallback } from "react";
import { FormikHelpers } from "formik";
import { toast } from "@jsq/ds/core";
import { JsqAccessTypeEnum } from "~/graphql/supergraphEnums";
import { useCreateArenaAccessMutation } from "~/graphql/supergraphHooks";
import { CreateArenaAccessInput } from "~/graphql/supergraphTypes";
import { toGlobalId } from "~/utils/graphql-helpers";

export type FormValues = {
  userIds: string[];
  roleGroupName: string;
  accessType: JsqAccessTypeEnum | "";
  expirationDate: Date | null;
};

export const useAddUserModal = ({
  arenaId,
  arenaIds,
  closeModal,
}: {
  arenaId?: string;
  arenaIds?: string[];
  closeModal: () => void;
}) => {
  const [createArenaAccess, { loading }] = useCreateArenaAccessMutation();
  const initialValues: FormValues = {
    userIds: [],
    roleGroupName: "",
    accessType: "",
    expirationDate: null,
  };

  const createWorkspaceAccess = useCallback(
    async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
      if (!values.accessType) {
        toast.error("Access type is required");
        return;
      }

      const globalUserIds = values.userIds.map((id) => toGlobalId("UserObjectType", id));
      const input: CreateArenaAccessInput = {
        userIds: globalUserIds,
        accessType: values.accessType,
        roleGroupName: values.roleGroupName,
        startDate: new Date().toISOString(),
        endDate: values.expirationDate?.toISOString() || null,
      };

      if (arenaIds) {
        input["arenaIds"] = arenaIds;
      } else if (arenaId) {
        input["arenaId"] = arenaId;
      }

      try {
        const response = await createArenaAccess({
          fetchPolicy: "network-only",
          variables: {
            input,
          },
          refetchQueries: ["ArenaAccessForArena", "GetWorkspaces"],
        });
        const data = response.data?.createArenaAccess;

        if (data?.__typename === "CreateArenaAccessResponse") {
          toast.success("User assigned to workspace");
          resetForm();
          closeModal();
        } else if (data?.__typename === "MutationErrorResponse") {
          toast.error(data.message);
        } else {
          toast.error("Failed to assign user to workspace");
        }
      } catch {
        toast.error("Failed to assign user to workspace");
      }
    },
    [createArenaAccess, arenaId, arenaIds, closeModal],
  );

  return {
    initialValues,
    createWorkspaceAccess,
    loading,
  };
};
